import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaHeart, FaBrain, FaLeaf, FaCheck, FaPlay, FaClock, FaUsers, FaShieldAlt, FaLightbulb } from 'react-icons/fa';
import { motion } from 'framer-motion';
import SEO from '../../components/SEO.jsx';
import nlsImage from '../../components/assets/Staff/NSL 1.jpg';
import nls2Image from '../../components/assets/Staff/NSL 2.jpg';
import nls3Image from '../../components/assets/Staff/NSL 3.jpeg';

const NLSHunter = () => {
  const [activeSection, setActiveSection] = useState('overview');
  
  // Refs for smooth scrolling
  const overviewRef = useRef(null);
  const sessionRef = useRef(null);
  const explorationRef = useRef(null);
  const benefitsRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const refs = {
      'overview': overviewRef,
      'session': sessionRef,
      'exploration': explorationRef,
      'benefits': benefitsRef
    };
    
    const targetRef = refs[sectionId];
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
      setActiveSection(sectionId);
    }
  };

  // Scroll spy effect to highlight active section
  useEffect(() => {
    const handleScroll = () => {
      const refs = [
        { id: 'overview', ref: overviewRef },
        { id: 'session', ref: sessionRef },
        { id: 'exploration', ref: explorationRef },
        { id: 'benefits', ref: benefitsRef }
      ];

      const scrollPosition = window.scrollY + 100; // Offset for sticky nav

      for (let i = refs.length - 1; i >= 0; i--) {
        const { id, ref } = refs[i];
        if (ref.current && ref.current.offsetTop <= scrollPosition) {
          setActiveSection(id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const sections = [
    { id: 'overview', label: 'Awareness', icon: FaHeart },
    { id: 'session', label: 'Session Experience', icon: FaPlay },
    { id: 'exploration', label: 'What May Be Explored', icon: FaBrain },
    { id: 'benefits', label: 'Benefits', icon: FaLeaf }
  ];

  const explorationPoints = [
    "The energetic state of different body systems",
    "Patterns of tension, overactivity, or depletion",
    "How the body as a whole may be responding to lifestyle or environmental stress factors",
    "Resonance with natural supplements or supportive approaches",
    "Subtle shifts over time, especially when paired with conscious self-care"
  ];

  const benefits = [
    "More connected to their body's signals",
    "Reassured when navigating uncertainty",
    "Calmer around unanswered questions",
    "More confident in personal wellness choices",
    "Gently reminded that their experience matters"
  ];

  const targetAudience = [
    "Feel disconnected from their body's subtle signals",
    "Sense imbalance without clear explanation",
    "Experience ongoing stress, fatigue, or general overload",
    "Prefer gentler, non-directive approaches to wellbeing",
    "Wish to complement existing self-care or wellness practices",
    "Value awareness, prevention, and conscious living"
  ];

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-br from-white via-yellow/5 to-gold/5">
      <SEO title="NLS Hunter" description="NLS Hunter therapy at Bioresonance Leicester – gentle, non-invasive exploration of body signals and stress patterns. Complementary wellness at 108 New Walk." path="/therapy/nls-hunter" />
      {/* Breadcrumb */}
      <nav className="container mx-auto px-4 py-6">
        <ol className="flex items-center space-x-2 text-sm text-dark-grey">
          <li><Link to="/" className="hover:text-gold transition-colors">Home</Link></li>
          <li className="text-gold">/</li>
          <li><Link to="/therapy" className="hover:text-gold transition-colors">Treatments</Link></li>
          <li className="text-gold">/</li>
          <li className="text-gold font-semibold">NLS Hunter</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold/10 via-transparent to-blue/10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="inline-flex items-center bg-gold/10 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-4">
                <FaHeart className="mr-2" />
                When the body whispers and you finally listen
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold text-dark-grey leading-tight" style={{ fontFamily: "Playfair Display, serif" }}>
                NLS Hunter
              </h1>
              
              <p className="text-xl text-dark-grey leading-relaxed">
                Have you ever felt that something in your body feels slightly off, even when everything appears fine?
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Not pain.<br />Not illness.<br />Just a quiet sense of imbalance. A knowing without words.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Before the body ever demands attention, it often whispers. NLS Hunter offers a gentle space to listen.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                This non-invasive, full-body bioenergetic scan offers a whole-body overview by reflecting the body's subtle electromagnetic responses. It creates a picture of patterns related to stress, adaptation, or depletion across the system as a whole. Not to label or define, but to invite awareness, curiosity, and a deeper relationship with your body.
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <div className="flex items-center text-sm text-dark-grey">
                  <FaClock className="text-gold mr-2" />
                  <span>60-90 minutes</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaShieldAlt className="text-gold mr-2" />
                  <span>Non-invasive</span>
                </div>
                <div className="flex items-center text-sm text-dark-grey">
                  <FaUsers className="text-gold mr-2" />
                  <span>All ages</span>
                </div>
              </div>
            </motion.div>

            {/* Image Placeholder */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative group">
                {/* Decorative background shapes */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gold/10 rounded-full opacity-60 group-hover:scale-110 transition-transform duration-500"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-blue/20 rounded-full opacity-40 group-hover:scale-125 transition-transform duration-700"></div>
                
                {/* Main image container - same photo as NLS Hunter card */}
                <div className="relative p-4">
                  <div className="relative overflow-hidden rounded-3xl h-96">
                    <img
                      src={nlsImage}
                      alt="NLS Hunter therapy"
                      className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                    />
                    {/* Gradient overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                  </div>
                  
                  {/* Creative border */}
                  <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-3 group-hover:rotate-0 transition-transform duration-500"></div>
                  <div className="absolute inset-2 border-2 border-white/50 rounded-3xl transform -rotate-1 group-hover:rotate-0 transition-transform duration-500"></div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute top-8 right-8 w-3 h-3 bg-gold rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100"></div>
                <div className="absolute bottom-8 left-8 w-2 h-2 bg-blue rounded-full opacity-0 group-hover:opacity-100 transition-all duration-500 delay-200"></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="hidden md:block py-8 bg-white/50 backdrop-blur-sm sticky top-20 z-30" aria-label="On this page">
        <div className="container mx-auto px-4">
          <nav className="flex flex-wrap justify-center gap-2" aria-label="Page sections">
            {sections.map((section) => (
              <button
                type="button"
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`flex items-center px-4 py-2 rounded-full font-semibold transition-all duration-300 ${
                  activeSection === section.id
                    ? 'bg-gold text-black shadow-lg transform scale-105'
                    : 'bg-white text-dark-grey hover:bg-gold/20 hover:text-gold border border-grey'
                }`}
                aria-current={activeSection === section.id ? 'location' : undefined}
              >
                <section.icon className="mr-2 text-sm" />
                {section.label}
              </button>
            ))}
          </nav>
        </div>
      </section>

      {/* Overview Section - Awareness. Not diagnosis. */}
      <section ref={overviewRef} className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-8 border border-gold/20">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0">
                    <FaLightbulb className="text-xl text-gold" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-dark-grey mb-3" style={{ fontFamily: "Playfair Display, serif" }}>
                      Awareness. Not diagnosis.
                    </h2>
                    <p className="text-gray-600 leading-relaxed">
                      NLS Hunter is not a medical or diagnostic device and does not replace professional healthcare or clinical testing.
                      It offers perspective, not promises. Insight, not instruction. A space free from fear, pressure, or judgement.
                      A quiet invitation to listen more deeply, trust more gently, and care more consciously.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Session Experience */}
      <section ref={sessionRef} className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
                    A session that asks nothing of you
                  </h2>
                  <div className="space-y-6 text-gray-600 leading-relaxed">
                    <p>There is nothing to prepare.</p>
                    <p>Nothing to explain.</p>
                    <p>Nothing to fix.</p>
                    <p>
                      You remain fully clothed, seated or reclining in a calm, unhurried space. Lightweight headphones register energetic feedback from the body as a whole, with no discomfort, no intrusion, and no effort.
                    </p>
                    <p>
                      Many people describe the experience as calming, grounding, even quietly profound. Some notice physical sensations or emotions gently surfacing. Others simply experience stillness, a rare moment to pause and reconnect.
                    </p>
                    <div className="bg-gold/10 rounded-xl p-6 border border-gold/20">
                      <p className="text-dark-grey font-semibold text-lg">
                        The body speaks.<br />You listen.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="relative">
                  <div className="relative group">
                    <div className="relative overflow-hidden rounded-3xl h-80">
                      <img
                        src={nls2Image}
                        alt="NLS Hunter session experience"
                        className="absolute inset-0 w-full h-full object-cover rounded-3xl"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent rounded-3xl"></div>
                    </div>
                    <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform rotate-2"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* What NLS Explores */}
      <section ref={explorationRef} className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                  What may be explored
                </h2>
                <p className="text-lg text-gray-600 max-w-4xl mx-auto mb-6">
                  This reflective, full-body approach may offer insight into the energetic state of different body systems, patterns of tension, overactivity, or depletion, and how the body as a whole may be responding to lifestyle or environmental stress factors. It may also reflect resonance with natural supplements or supportive approaches, as well as subtle shifts over time, especially when paired with conscious self-care.
                </p>
                <div className="bg-gradient-to-r from-gold/10 to-blue/10 rounded-2xl p-6 border border-gold/20 text-center max-w-2xl mx-auto">
                  <p className="text-dark-grey font-semibold">
                    Nothing is diagnosed.<br />Nothing is prescribed.<br />Information becomes invitation.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  {explorationPoints.map((point, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                    >
                      <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <FaCheck className="text-gold text-sm" />
                      </div>
                      <p className="text-gray-600">{point}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="relative">
                  <div className="relative group">
                    <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gold/20 to-blue/20 h-96">
                      <img
                        src={nls3Image}
                        alt="NLS session - what may be explored"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="absolute inset-0 border-4 border-gold/30 rounded-3xl transform -rotate-2 pointer-events-none"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Benefits */}
      <section ref={benefitsRef} className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-4xl font-bold text-dark-grey mb-4" style={{ fontFamily: "Playfair Display, serif" }}>
                  What people often take with them
                </h2>
                <p className="text-lg text-gray-600 mb-4">
                  Not answers, but understanding. Not solutions, but clarity.
                </p>
                <p className="text-lg text-gray-600">
                  Many people leave the session feeling more connected to their body's signals, reassured when navigating uncertainty, calmer around unanswered questions, more confident in personal wellness choices, and gently reminded that their experience matters.
                </p>
                <p className="text-lg text-gray-600 mt-4 italic">Sometimes, being heard is the first form of support.</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-bold text-dark-grey mb-6">You may come away feeling:</h3>
                  <div className="space-y-4">
                    {benefits.map((benefit, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                      >
                        <div className="w-6 h-6 bg-gold/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <FaCheck className="text-gold text-sm" />
                        </div>
                        <p className="text-gray-600">{benefit}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-2xl font-bold text-dark-grey mb-6">Who this experience may resonate with</h3>
                  <p className="text-gray-600 mb-6">This session may be meaningful for those who:</p>
                  <div className="space-y-4">
                    {targetAudience.map((item, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className="flex items-start space-x-3 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                      >
                        <div className="w-6 h-6 bg-blue/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <FaUsers className="text-blue text-sm" />
                        </div>
                        <p className="text-gray-600">{item}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

      {/* Call to Action */}
      <section className="py-16 bg-gradient-to-r from-gold/10 via-blue/10 to-gold/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-dark-grey mb-6" style={{ fontFamily: "Playfair Display, serif" }}>
            Ready to Experience NLS Hunter?
          </h2>
          <p className="text-xl text-dark-grey mb-8 max-w-3xl mx-auto">
            When the body whispers, NLS Hunter offers a gentle space to listen – and to connect with your body's signals with more awareness, trust, and care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.sumupbookings.com/t-p-wellness-ltd"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-gold to-yellow hover:from-yellow hover:to-gold text-black font-bold py-4 px-8 rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            >
              Schedule Your Session
            </a>
            <Link
              to="/prices"
              className="bg-transparent border-2 border-gold text-gold hover:bg-gold hover:text-black font-bold py-4 px-8 rounded-full transition-all duration-300"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 bg-gold hover:bg-gold/90 text-black p-3 rounded-full shadow-lg transition-all duration-300 transform hover:scale-110 z-50"
        aria-label="Back to top"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    </div>
  );
};

export default NLSHunter;
